import React, { useMemo } from 'react';
import ReactDOM from 'react-dom';

import App from 'App';
import { format } from 'date-fns';
import { Formik, useFormikContext } from 'formik';

import ExportPackModal, { exportBlocker } from './ExportPackModal';
import IssuePackModal, { issueBlocker } from './IssuePackModal';
import { ManagementPackProvider, useManagementPackContext } from './ManagementPackContext';
import { initialValuesFor, isLatestIssued, scopeSignatureFor, SNAPSHOT_COMPLETED, SNAPSHOT_FAILED, validationSchema } from './managementPackUtils';
import PackPreview from './preview/PackPreview';
import PackSettingsTab from './tabs/PackSettingsTab';
import SaveAndExportTab from './tabs/SaveAndExportTab';
import SectionsTab from './tabs/SectionsTab';
import StatusesTab from './tabs/StatusesTab';

import { formatDateLong } from 'Helpers/dateFormat';
import { getDateFnsLocale } from 'Helpers/localeService';

import Alert from 'CommonComponents/Alert';
import Badge from 'CommonComponents/Badge';
import Button from 'CommonComponents/Button';
import Tabs from 'CommonComponents/Tabs';

import { ConfirmationModalProvider } from 'Context/ConfirmationModalContext';

import { useConfirmationModal } from 'Hooks/useConfirmationModal';
import useModal from 'Hooks/useModal';

import RefreshSyncIcon from 'Assets/icons/refresh-sync.svg';

const PackStatus = () => {
    const { activePack } = useManagementPackContext();

    if (!activePack) {
        return <Badge variant="neutral">{$t('Not generated')}</Badge>;
    }

    return (
        <>
            {activePack.isIssued ? (
                <Badge variant="success">{$t('Issued {{date}}', { date: formatDateLong(activePack.issuedAt) })}</Badge>
            ) : (
                <Badge variant="info">{$t('Draft')}</Badge>
            )}
            {activePack.isSuperseded && <Badge variant="warning">{$t('Superseded')}</Badge>}
            {activePack.revision > 1 && <Badge variant="neutral">{$t('Revision {{revision}}', { revision: activePack.revision })}</Badge>}
        </>
    );
};

const SnapshotProgress = () => {
    const { activePack } = useManagementPackContext();

    if (!activePack || activePack.isIssued) {
        return null;
    }

    if (activePack.snapshotStatus === SNAPSHOT_FAILED) {
        return (
            <Alert type="error">
                {$t('The pack data could not be captured.')} {activePack.snapshotError}
            </Alert>
        );
    }

    if (activePack.snapshotStatus === SNAPSHOT_COMPLETED) {
        return null;
    }

    const progress = Math.max(0, Math.min(100, activePack.snapshotProgress || 0));

    return (
        <div role="status" className="tw-flex tw-flex-col tw-gap-1.5">
            <div className="tw-flex tw-justify-between tw-text-sm tw-text-charcoal-75">
                <span>{$t('Capturing the pack data')}</span>
                <span>{progress}%</span>
            </div>
            <div
                className="tw-h-1.5 tw-w-full tw-rounded-full tw-bg-charcoal-15 tw-overflow-hidden"
                role="progressbar"
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={progress}
                aria-label={$t('Capturing the pack data')}
            >
                <div className="tw-h-full tw-bg-blue tw-transition-all" style={{ width: `${progress}%` }} />
            </div>
        </div>
    );
};

const GenerateButton = () => {
    const { values } = useFormikContext();
    const { activePack, generating, generate, options } = useManagementPackContext();

    const running = activePack && !activePack.isIssued && ![SNAPSHOT_COMPLETED, SNAPSHOT_FAILED].includes(activePack.snapshotStatus);
    // A pack whose scope key differs from the one this user would build now:
    // generating here would build that pack instead of refreshing this one.
    const otherScope = !!activePack && activePack.scopeSignature !== scopeSignatureFor(values.scope, options);

    if (activePack?.isIssued) {
        return null;
    }

    return (
        <Button
            variant="secondary"
            onClick={generate}
            disabled={!options.canGenerate || generating || running || otherScope}
            loading={generating}
            title={otherScope ? $t("This pack's scope differs from the one you would build now, so it cannot be refreshed from here.") : undefined}
        >
            {!generating && <RefreshSyncIcon className="tw-w-4 tw-h-4" aria-hidden="true" />}
            {activePack ? $t('Refresh data') : $t('Generate pack')}
        </Button>
    );
};

/**
 * One line under the title: the period and as-at date, when the data was
 * captured, and the configuration in use.
 */
const PackMeta = () => {
    const { values } = useFormikContext();
    const { activePack, options, savedSets, appliedFilterSetId } = useManagementPackContext();

    const period = (options.periods || []).find((option) => option.value === values.settings.period);
    const setName = savedSets.find((set) => set.id === (activePack?.reportFilterSetId ?? appliedFilterSetId))?.name;

    const parts = [
        period ? $t('{{period}} as at {{date}}', { period: period.label, date: formatDateLong(values.settings.as_at) }) : null,
        activePack?.generatedAt
            ? $t('data captured {{date}}', { date: format(new Date(activePack.generatedAt), 'd MMM yyyy HH:mm', { locale: getDateFnsLocale() }) })
            : null,
        setName ? $t('configuration: {{name}}', { name: setName }) : null,
    ].filter(Boolean);

    return <p className="tw-text-sm tw-text-charcoal-75 tw-m-0">{parts.join(' · ')}</p>;
};

/**
 * Opens a draft revision of the latest issued pack, after confirmation.
 */
const ReissueButton = () => {
    const { activePack, packs, options, reissuing, reissuePack } = useManagementPackContext();
    const confirm = useConfirmationModal();

    if (!isLatestIssued(activePack, packs)) {
        return null;
    }

    const reissue = () =>
        confirm({
            title: $t('Reissue this pack?'),
            message: $t(
                "A draft revision {{revision}} opens with this pack's settings, commentary and manual statuses, and its data is captured again. This pack stays issued until the revision is issued.",
                { revision: activePack.revision + 1 },
            ),
            actions: [
                { confirmed: false, label: $t('Cancel'), variant: 'secondary' },
                { confirmed: true, label: $t('Reissue'), variant: 'primary', onClick: () => reissuePack() },
            ],
        });

    return (
        <Button variant="secondary" onClick={reissue} disabled={!options.canGenerate || reissuing} loading={reissuing}>
            {$t('Reissue')}
        </Button>
    );
};

/**
 * Save the configuration to the draft, issue it, and open the export dialog.
 */
const PackActions = () => {
    const context = useManagementPackContext();
    const { activePack, saving, saveToPack, exporting, issuing, options } = context;
    const exportModal = useModal();
    const issueModal = useModal();
    const blocker = exportBlocker(context);
    const issueBlocked = issueBlocker(context);

    return (
        <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
            <GenerateButton />
            {activePack && !activePack.isIssued && options.canGenerate && (
                <Button variant="secondary" onClick={saveToPack} disabled={saving} loading={saving}>
                    {$t('Save to pack')}
                </Button>
            )}
            <ReissueButton />
            {activePack && !activePack.isIssued && options.canGenerate && (
                <Button
                    variant="secondary"
                    onClick={issueModal.show}
                    disabled={!!issueBlocked || issuing || exporting}
                    loading={issuing}
                    title={issueBlocked || undefined}
                >
                    {$t('Issue pack')}
                </Button>
            )}
            {options.canGenerate && (
                <Button variant="primary" onClick={exportModal.show} disabled={!!blocker || exporting} loading={exporting} title={blocker || undefined}>
                    {$t('Export PowerPoint')}
                </Button>
            )}
            <ExportPackModal modal={exportModal} />
            <IssuePackModal modal={issueModal} />
        </div>
    );
};

const SlideCount = () => {
    const { preview } = useManagementPackContext();

    if (!preview?.slideCount) {
        return null;
    }

    return (
        <p className="tw-text-xs tw-text-charcoal-75 tw-m-0 tw-pt-4 tw-border-t tw-border-charcoal-15">
            {$t('{{count}} slides', { count: preview.slideCount })}
        </p>
    );
};

/**
 * The monthly management pack builder: settings in four tabs beside a
 * slide-accurate preview of the pack.
 */
const ManagementPackBuilder = () => {
    const tabs = useMemo(
        () => [
            { id: 'pack-settings', title: $t('Pack settings'), content: () => <PackSettingsTab /> },
            { id: 'pack-sections', title: $t('Sections'), content: () => <SectionsTab /> },
            { id: 'pack-statuses', title: $t('Statuses'), content: () => <StatusesTab /> },
            { id: 'pack-save-export', title: $t('Save and export'), content: () => <SaveAndExportTab /> },
        ],
        [],
    );

    return (
        <div className="tw-flex-auto tw-flex tw-flex-col tw-px-6 tw-py-6.5 tw-rounded-lg tw-bg-white tw-shadow-primary tw-space-y-7">
            <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-3">
                <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-x-3 tw-gap-y-1">
                    <h2 className="tw-text-lg tw-font-semibold tw-text-charcoal tw-m-0">{$t('Monthly management pack')}</h2>
                    <PackStatus />
                    <PackMeta />
                </div>
                <PackActions />
            </div>

            <SnapshotProgress />

            <div className="tw-grid tw-grid-cols-1 xl:tw-grid-cols-[26rem_minmax(0,1fr)] tw-gap-7">
                <aside className="tw-min-w-0 tw-flex tw-flex-col" aria-label={$t('Pack builder')}>
                    <Tabs tabs={tabs} />
                    <SlideCount />
                </aside>
                <div className="tw-min-w-0 tw-rounded-lg tw-bg-background tw-p-5">
                    <PackPreview />
                </div>
            </div>
        </div>
    );
};

export default ManagementPackBuilder;

const rootEl = document.getElementById('ManagementPackBuilder');

if (rootEl) {
    const parse = (attribute, fallback) => {
        try {
            return JSON.parse(rootEl.getAttribute(attribute)) ?? fallback;
        } catch (e) {
            return fallback;
        }
    };

    const options = parse('data-options', {});
    const urls = parse('data-urls', {});
    const channels = parse('data-channels', {});

    ReactDOM.render(
        <App>
            <ConfirmationModalProvider>
                <Formik initialValues={initialValuesFor(options)} validationSchema={validationSchema()} onSubmit={() => {}}>
                    <ManagementPackProvider options={options} urls={urls} channels={channels}>
                        <ManagementPackBuilder />
                    </ManagementPackProvider>
                </Formik>
            </ConfirmationModalProvider>
        </App>,
        rootEl,
    );
}
